import { fabric } from 'fabric';
import { uniqueId } from 'lodash';
import { useCallback, useContext, useEffect, useLayoutEffect, useRef } from 'react';
import Vec2 from 'vec2';

import {
    CANVAS_CURSOR,
    autoZoomPanToFit,
    cleanSkeletonPoints,
    formatCornerRadiusLabel,
    generateSkeletonData,
    getOutlineEdges,
    getOutlinePointsFromModifiedShape,
    getOutlinePointsFromShape,
    getOutlinePts,
    isPointInsidePolygon,
    updateShapeByEdge,
    updateShapeByTranslation,
} from '@canvas-common';
import {
    APPLIANCE_SELECTION_BORDER_THICKNESS,
    AUTO_ZOOM_DELAY,
    AUTO_ZOOM_DELAY_LOOPS,
    BACKSPLASH_INPUT_LIMIT,
    BACKSPLASH_LABEL,
    BUMPOUT_CLOCKWISE_REMAINDER,
    BUMPOUT_COUNTERCLOCKWISE_REMAINDER,
    BUMPOUT_LENGTH,
    CANVAS_OBJECT_TYPE_PREFIX,
    CANVAS_OBJECT_TYPE_SUFFIX,
    DEFAULT_BUMPOUT_DEPTH,
    DEFAULT_BUMPOUT_LENGTH,
    DEFAULT_CORNER_RADIUS,
    DEFAULT_DEPTH,
    DEFAULT_EXTERNAL_DIAGONAL_CORNER_LENGTH,
    DEFAULT_INTERNAL_DIAGONAL_CORNER_LENGTH,
    DEFAULT_RADIUS_SAGITTA,
    DEFAULT_ZOOM,
    DESIGN_COUNTERTOP_ACTIONS_HEIGHT,
    DIAGONAL_CORNER_OPTIONS,
    INCH_QUOTE,
    MAX_RADIUS_SAGITTA,
    SELECTION_CIRCLES,
    WATERFALL_INPUT_LIMIT,
    WATERFALL_LABEL,
} from '@common/constants';
import {
    CustomSideType,
    DRAGGING_STATE,
    ShapeType,
    SideItemOption,
    containsBumpOut,
    containsRadiusOut,
} from '@common/enums';
import { DrawingTabEnum } from '@common/enums/drawing-tab.enum';
import { MathFunctions, getCornerType, roundToArrowLengthGranularity } from '@common/helpers';
import { useShapeArea } from '@common/hooks/useShapeArea/useShapeArea';
import {
    ApplianceModel,
    AppliancesAllowedOnShapes,
    AppliancesAllowedOutsideShapes,
    ArrowInfo,
    CornerInfo,
    DiagonalCornerModel,
    DiagonalCornerType,
    DrawingEdge,
    DrawingShape,
    PredefinedShape,
    getDefaultRadiusFromRadii,
    isCornerRadiusModel,
} from '@common/models';
import { adjustValueForMinMax, calculateBaseShapeArea } from '@common/utils';
import useWindowSize from '@common/utils/useWindowSize';
import { useAnalyticsEvents } from '@core/hooks/useAnalyticsEvents';
import { useSideItemBoundsNotification } from '@core/hooks/useSideItemBoundsNotification';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { useCornerRadii } from '@ee-api/corner-radii-api/hooks/useCornerRadii';
import { useSelectedCornerRadius } from '@ee-api/corner-radii-api/hooks/useSelectedCornerRadius';
import { useSelectedEdge } from '@ee-api/edges-api/hooks/useSelectedEdge';
import { DrawingShapeContext, isDrawingShape, useApplianceContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { selectSelectedCustomSide } from '@redux/slices/customSideSlice';
import { selectSelectedDiagonalCorner } from '@redux/slices/diagonalCornerSlice';
import {
    addClearedShapeEditNotification,
    selectActiveDrawingAppliance,
    selectApplianceTriggerToggle,
    selectNeedRedrawCanvas,
    selectPauseUndoStackUpdatesForSummary,
    selectSkipUndoStackUpdates,
    setNeedRedrawCanvas,
    setPreventRepricing,
    setSkipUndoStackUpdates,
} from '@redux/slices/drawingShapeSlice';
import {
    selectActiveFreeDrawing,
    selectFabricCanvas,
    selectSelectionCircles,
    selectTriggerZooming,
    updateActiveFreeDrawing,
    updateCanvas,
} from '@redux/slices/fabricSlice';
import { updatePrevCounterTopAreaQty } from '@redux/slices/pricedProductsSlice';
import { selectSelectedDrawingTab, updateSelectedDrawingTab } from '@redux/slices/shapeSlice';
import { selectIsManualZoom } from '@redux/slices/toolbarCanvasSlice';

import useAppliance from '../../appliances/useAppliance';
import drawingTool from '../drawer/drawingTool';
import { EditableLabel, getCornerRadiusFromSize } from '../drawer/editableLabel';
import useSideItems from './useSideItems';
import useSlabImgElement from './useSlabImgElement';

import colors from '../drawing-tabs/colors.module.scss';
import './fabricCanvas.scss';

export interface FabricCanvasProps {
    width: number;
}

type Position = { x: number | undefined; y: number | undefined };

const emptyArray: DrawingShape[] = [];

/**
 * try and parse a JSON string and return undefined if an error
 * @param value the _possible_ json string
 */
const tryJsonParse = (value: string | undefined) => {
    try {
        return value ? JSON.parse(value) : undefined;
    } catch (error) {
        return undefined;
    }
};

const FabricCanvas = ({ width }: FabricCanvasProps): JSX.Element => {
    const canvasRef = useRef(null);
    const dispatch = useAppDispatch();

    const activeFreeDrawing = useAppSelector(selectActiveFreeDrawing);
    const canvas = useAppSelector(selectFabricCanvas);
    const selectionCircles = useAppSelector(selectSelectionCircles);

    const selectedDrawingTab = useAppSelector(selectSelectedDrawingTab);

    const {
        drawingShapes,
        updateDrawingShapes,
        activeDrawingShape,
        activeDrawingItem,
        updateActiveDrawingShape,
        updateActiveDrawingAppliance,
        pushDrawingShapesUndoStack,
        autoPlaceShapeTranslation,
        areThereShapesInTheDrawingArea,
        updateCustomSideAction,
    } = useContext(DrawingShapeContext);

    const applianceTriggerToggle = useAppSelector(selectApplianceTriggerToggle);
    const activeDrawingAppliance = useAppSelector(selectActiveDrawingAppliance);
    const needRedrawCanvas = useAppSelector(selectNeedRedrawCanvas);
    const pauseUndoStackUpdatesForSummary = useAppSelector(selectPauseUndoStackUpdatesForSummary);
    const skipUndoStackUpdates = useAppSelector(selectSkipUndoStackUpdates);

    const toggleTriggerZooming = useAppSelector(selectTriggerZooming);

    const slabImgElement = useSlabImgElement();
    const { selectedValue: selectedColor } = useSelectedColor();

    const { isDraggingAppliance, updateIsDraggingAppliance } = useApplianceContext();
    const { cornerRadii } = useCornerRadii();
    const { selectedValue: selectedCornerRadius } = useSelectedCornerRadius();

    const { selectedValue: selectedEdge } = useSelectedEdge();
    const isManualZoom = useAppSelector(selectIsManualZoom);
    const selectedDiagonalCorner = useAppSelector(selectSelectedDiagonalCorner);

    const selectedCustomSide = useAppSelector(selectSelectedCustomSide);

    const isSelectedByUser = selectedCornerRadius !== undefined;
    const selectedCornerRadiusOnCard = selectedCornerRadius;

    const { updateApplianceByTranslation } = useAppliance();

    const drawingShapesRef = useRef(emptyArray);
    drawingShapesRef.current = drawingShapes;
    const { selectedMaterialAreaType } = useShapeArea();

    const { trackAddShape } = useAnalyticsEvents();

    const selectedDrawingTabRef = useRef<DrawingTabEnum>();
    selectedDrawingTabRef.current = selectedDrawingTab;

    const [, windowHeight] = useWindowSize();

    const initCanvas = useCallback(
        (canvasElement: HTMLCanvasElement) => {
            const canvasOptions: fabric.ICanvasOptions = {
                preserveObjectStacking: false,
                selection: true,
                defaultCursor: 'default',
                fireRightClick: false, // <-- disable firing of right click events
                stopContextMenu: true, // <--  prevent context menu from showing
            };

            const canvas = new fabric.Canvas(canvasElement, canvasOptions);
            canvas.renderAll();
            canvas.setZoom(DEFAULT_ZOOM);
            dispatch(updateCanvas(canvas));
            // Selection box border properties
            // ----------------------------------------
            fabric.Object.prototype.set({
                borderColor: colors.secondaryColor,
                borderScaleFactor: APPLIANCE_SELECTION_BORDER_THICKNESS,
                objectCaching: true,
            });
        },
        [dispatch]
    );

    useEffect(() => {
        if (!canvas) {
            return;
        }

        if (isDraggingAppliance) {
            // This is where we would redraw to highlight shapes
            // see PR https://bitbucket.org/inscythinc/easyestimator-react/pull-requests/1012
            canvas.moveCursor = CANVAS_CURSOR.FREE_DRAWING.POINTER;
        } else {
            canvas.defaultCursor =
                selectedDrawingTab === DrawingTabEnum.FREE_DRAWING_TAB
                    ? CANVAS_CURSOR.FREE_DRAWING.DEFAULT
                    : CANVAS_CURSOR.DEFAULT.HOVER;
        }
    }, [isDraggingAppliance, canvas, selectedDrawingTab]);

    useLayoutEffect(() => {
        if (canvasRef?.current) {
            initCanvas(canvasRef.current);
        }

        return () => {
            canvasRef.current = null;
        };
    }, [initCanvas]);

    /**
     * update a cornerInfo record with new radius corner product data
     * will remove diagonal length, and set the labels
     * WARNING: MUTATES corner Object
     * @param corner
     * @param product
     * @param size use undefined to select the default value from the product, otherwise this size is used
     */
    const setCornerRadiusSize = useCallback(
        (corner: CornerInfo, size: number): void => {
            const actualProduct = getCornerRadiusFromSize(size ?? DEFAULT_CORNER_RADIUS, cornerRadii);

            // if there's no product matching that value, return to default
            // this happens if the constraints are met, but there are no radius corner products available
            corner.size = actualProduct ? size ?? DEFAULT_CORNER_RADIUS : DEFAULT_CORNER_RADIUS;

            // this isn't technically necessary as the productId check happens in pricedProducts as well
            corner.productId = actualProduct?.productId;
            corner.label = actualProduct?.name;

            delete corner.diagonalLength; // makes it a regular corner again
            corner.hasPredefinedExtraEdging = false; // if it had this before, it needs to be false now

            const radiusLabel = formatCornerRadiusLabel(corner.size);
            if (!corner.labelMeasure?.text) {
                corner.labelMeasure = EditableLabel.newMeasureText(radiusLabel, corner.labelMeasure?.fill);
            }
            if (!corner.labelText?.text) {
                corner.labelText = EditableLabel.newLabelText(radiusLabel);
            }
            corner.labelMeasure.set('text', radiusLabel);
            corner.labelText.set('text', radiusLabel);
        },
        [cornerRadii]
    );

    const updateCornerRecordWithDiagonalProduct = (corner: CornerInfo, product: DiagonalCornerModel): void => {
        const isExternalCorner = corner.angle! > 0;
        corner.diagonalLength = isExternalCorner
            ? DEFAULT_EXTERNAL_DIAGONAL_CORNER_LENGTH
            : DEFAULT_INTERNAL_DIAGONAL_CORNER_LENGTH;

        corner.productId = product?.productId;
        corner.label = product?.name || '';

        corner.labelMeasure?.set('text', `${corner.diagonalLength}${INCH_QUOTE}`);
        corner.labelText?.set('text', corner.label);
    };

    const removeDiagonalCornerOnShape = useCallback(
        (corner: CornerInfo): void => {
            setCornerRadiusSize(corner, DEFAULT_CORNER_RADIUS);
        },
        [setCornerRadiusSize]
    );

    const handleCornerSelection = useCallback(
        (e: fabric.IEvent<MouseEvent>): void => {
            if (!e.target) {
                return;
            }
            const corner: CornerInfo = e.target.data?.parentDrawingShape?.cornerMap[e.target.data.cornerId];
            const cornerProduct = e.target.data?.selectedOption;
            canvas.remove(e.target);

            // applying roundness on corner radius or diagonal corner
            if (isCornerRadiusModel(cornerProduct)) {
                setCornerRadiusSize(
                    corner,
                    Math.min(corner.maxAllowedRadius ?? 0, getDefaultRadiusFromRadii(cornerProduct))
                );
            } else {
                const cornerType = getCornerType(e.target.data.selectedOption);
                if (cornerType === DIAGONAL_CORNER_OPTIONS.REMOVE_DIAGONAL_CORNER && corner.diagonalLength) {
                    removeDiagonalCornerOnShape(corner);
                } else {
                    updateCornerRecordWithDiagonalProduct(corner, cornerProduct);
                }
            }
        },
        [canvas, removeDiagonalCornerOnShape, setCornerRadiusSize]
    );

    const handleEdgeSelection = useCallback(
        (e: fabric.IEvent<MouseEvent>) => {
            if (!e.target) {
                return;
            }

            const edges: DrawingEdge[] = Object.values(e.target.data?.parentDrawingShape?.drawingEdges);
            const corners: CornerInfo[] = Object.values(e.target.data?.parentDrawingShape?.cornerMap);
            canvas.remove(e.target);

            let disable = true;
            const prevEdge = edges.find((edge) => edge.id === e.target?.data.edgeId);

            if (!prevEdge) {
                return;
            }

            if (e.target.data.selectedOption.type === CustomSideType.RADIUS_OUT) {
                prevEdge.customSideType = CustomSideType.RADIUS_OUT;
                prevEdge.radiusSagitta = DEFAULT_RADIUS_SAGITTA;
            } else if (e.target.data.selectedOption.type === CustomSideType.BUMP_OUT) {
                prevEdge.customSideType = CustomSideType.BUMP_OUT;
                if (prevEdge.bumpOut) {
                    prevEdge.bumpOut.length = DEFAULT_BUMPOUT_LENGTH;
                    prevEdge.bumpOut.depth = DEFAULT_BUMPOUT_DEPTH;
                }
            } else if (e.target.data.selectedOption.type === CustomSideType.REMOVE_CUSTOM_SIDE) {
                prevEdge.customSideType = undefined;
                prevEdge.realRadius = undefined;
                disable = false;

                if (prevEdge.bumpOut) {
                    prevEdge.bumpOut.length = 0;
                    prevEdge.bumpOut.clockwiseRemainderLength = 0;
                    prevEdge.bumpOut.counterClockwiseRemainderLength = 0;
                }
            } else {
                const startCorner = corners.find((corner) => corner.id === prevEdge.startCornerId);
                const endCorner = corners.find((corner) => corner.id === prevEdge.endCornerId);

                if (!startCorner || !endCorner) {
                    return;
                }

                startCorner.label = DIAGONAL_CORNER_OPTIONS.REMOVE_DIAGONAL_CORNER;
                startCorner.productId = undefined;
                removeDiagonalCornerOnShape(startCorner);

                startCorner.size = DEFAULT_CORNER_RADIUS;
                endCorner.label = DIAGONAL_CORNER_OPTIONS.REMOVE_DIAGONAL_CORNER;
                endCorner.productId = undefined;
                removeDiagonalCornerOnShape(endCorner);

                endCorner.size = DEFAULT_CORNER_RADIUS;
            }

            updateCustomSideAction({
                shape: e.target.data?.parentDrawingShape,
                edge: prevEdge,
                disable,
            });
        },
        [canvas, removeDiagonalCornerOnShape, updateCustomSideAction]
    );

    // on dropping appliances
    const onDrop = useCallback(
        (e: fabric.IEvent<MouseEvent | DragEvent>) => {
            e.e.stopPropagation();
            e.e.preventDefault();

            updateIsDraggingAppliance(false);
            dispatch(setSkipUndoStackUpdates(true));

            const passedApplianceAsString = (e.e as DragEvent)?.dataTransfer?.getData('text');
            const passedAppliance: ApplianceModel | undefined = tryJsonParse(passedApplianceAsString);
            if (!passedAppliance) {
                // this occurs when you drag/drop something else, like an edge, onto the design
                return;
            }

            const centerPoint = new Vec2(canvas.getPointer(e.e));
            let droppedShapeAccepted = false;

            // attempt to accept the dropped appliance onto a shape
            drawingShapesRef.current
                ?.filter((shape: DrawingShape) => shape.type !== ShapeType.FAUX_SHAPE)
                .forEach((shape: DrawingShape) => {
                    if (
                        isPointInsidePolygon(getOutlinePointsFromModifiedShape(shape), centerPoint) &&
                        AppliancesAllowedOnShapes.indexOf(passedAppliance.applianceType) > -1
                    ) {
                        droppedShapeAccepted = true;
                        dispatch(setSkipUndoStackUpdates(false));
                        const newApplianceId = uniqueId();
                        shape.appliances.push({
                            id: newApplianceId,
                            applianceModel: passedAppliance,
                            centerPoint: centerPoint,
                            angle: 0,
                            height: passedAppliance.width,
                            width: passedAppliance.length,
                        });
                        updateActiveDrawingAppliance(`${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}_${newApplianceId}`);
                    }
                });

            // if not accepted onto a shape, attempt to accept appliance onto faux shape (anywhere)
            if (!droppedShapeAccepted) {
                // TODO simplify the below to remove loop and hit first index of drawingShapes directly
                drawingShapesRef.current
                    ?.filter((shape: DrawingShape) => shape.type === ShapeType.FAUX_SHAPE)
                    .map((shape: DrawingShape) => {
                        if (AppliancesAllowedOutsideShapes.indexOf(passedAppliance.applianceType) > -1) {
                            dispatch(setSkipUndoStackUpdates(false));
                            const newApplianceId = uniqueId();
                            shape.appliances.push({
                                id: newApplianceId,
                                applianceModel: passedAppliance,
                                centerPoint: centerPoint,
                                angle: 0,
                                height: 0,
                                width: 0,
                            });
                            updateActiveDrawingAppliance(`${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}_${newApplianceId}`);
                        }
                    });
            }
            updateDrawingShapes([...drawingShapesRef.current]);
            dispatch(setNeedRedrawCanvas(true));
            canvas.renderAll();
        },
        [canvas, dispatch, updateActiveDrawingAppliance, updateDrawingShapes, updateIsDraggingAppliance]
    );

    const lastPosition = useRef<Position>({ x: undefined, y: undefined }); // previous position for delta calculations
    const draggingState = useRef(DRAGGING_STATE.IDLE);
    const skipShapeLogic = useRef(false);
    const movingItem = useRef<DrawingShape | string | undefined>(undefined);
    const shapeStartDrag = useRef<Vec2>(new Vec2(0, 0));

    // @PaulP this solves https://inscyth-inc.atlassian.net/browse/EE-612
    // maybe more advance selection logic should be done in the future
    // Moved to its own useEffect for EZE-1333 bug
    useEffect(() => {
        canvas.selection = false;
    }, [canvas]);

    // TODO this shows the list of event handlers for the canvas, keep it around just in case for now
    // useEffect(() => {
    //     console.log('==handlers', canvas?.__eventListeners);
    // }, [canvas.__eventListeners]);

    const onMouseDownBefore = useCallback(
        // eslint-disable-next-line sonarjs/cognitive-complexity
        (e: fabric.IEvent<MouseEvent>) => {
            // fix for EE-1002
            if (e?.target?.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE)) {
                const pointer = new Vec2(canvas.getPointer(e.e));
                drawingShapesRef.current.map((shape: DrawingShape) => {
                    // check to see if click is inside the actual counter polygon
                    if (isPointInsidePolygon(getOutlinePointsFromShape(shape), pointer)) {
                        updateActiveDrawingShape(shape);
                        dispatch(setNeedRedrawCanvas(true));
                    }
                });
            } else {
                skipShapeLogic.current = false;
                drawingShapesRef.current.map((shape: DrawingShape) => {
                    if (e.target?.name === shape?.canvasObjectName + CANVAS_OBJECT_TYPE_SUFFIX.SHAPE_GROUP) {
                        const groupMemberClicked = e.subTargets?.length ? e.subTargets[0] : null;
                        if (groupMemberClicked?.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE)) {
                            e.target.lockMovementX = true;
                            e.target.lockMovementY = true;
                            shape.fabricGroup.remove(groupMemberClicked);
                            updateActiveDrawingAppliance(groupMemberClicked.name);
                            drawingTool.addAppliancesToCanvas(canvas, shape, groupMemberClicked.name);
                            skipShapeLogic.current = true;
                            movingItem.current = groupMemberClicked.name;
                            shapeStartDrag.current = new Vec2(canvas.getPointer(e.e));
                            dispatch(setNeedRedrawCanvas(true));
                        }
                    }
                });
            }
        },
        [canvas, dispatch, updateActiveDrawingAppliance, updateActiveDrawingShape]
    );

    const onMouseDown = useCallback(
        // eslint-disable-next-line sonarjs/cognitive-complexity
        (e: fabric.IEvent<MouseEvent>) => {
            if (e.target) {
                if (e?.target?.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE)) {
                    updateActiveDrawingAppliance(e.target.name);
                    movingItem.current = e.target.name;
                    shapeStartDrag.current = new Vec2(canvas.getPointer(e.e));
                    dispatch(setNeedRedrawCanvas(true));

                    canvas.moveCursor = CANVAS_CURSOR.FREE_DRAWING.POINTER;
                } else if (e?.target?.name?.includes(CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE)) {
                    if (e.target.data.cornerId) {
                        handleCornerSelection(e);
                    } else {
                        handleEdgeSelection(e);
                    }
                    updateDrawingShapes([...drawingShapesRef.current]);
                    dispatch(setNeedRedrawCanvas(true));
                    canvas.renderAll();
                } else {
                    drawingShapesRef.current?.map((shape: DrawingShape) => {
                        if (
                            e.target?.name === shape?.canvasObjectName + CANVAS_OBJECT_TYPE_SUFFIX.SHAPE_GROUP &&
                            !skipShapeLogic.current
                        ) {
                            const pointer = new Vec2(canvas.getPointer(e.e));
                            shapeStartDrag.current = pointer;
                            drawingTool.removeObjectByName(
                                canvas,
                                `${CANVAS_OBJECT_TYPE_PREFIX.SHAPE_LABEL}_${shape.label}`,
                                true
                            );
                            drawingTool.removeObjectByName(
                                canvas,
                                `${CANVAS_OBJECT_TYPE_PREFIX.EDGE_NAME}_${shape.label}`,
                                true
                            );
                            drawingTool.removeObjectByName(
                                canvas,
                                `${CANVAS_OBJECT_TYPE_PREFIX.INTERIOR_ARROW}`,
                                false
                            );
                            drawingTool.removeObjectByName(canvas, `${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}`, false);

                            shape.hideAllLabels();
                            // check to see if click is inside the actual counter polygon
                            if (isPointInsidePolygon(getOutlinePointsFromShape(shape), pointer)) {
                                // beginning of moving a shape
                                movingItem.current = shape;
                                drawingTool.removeObjectByName(canvas, CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE);
                                updateActiveDrawingShape(shape);
                                dispatch(setNeedRedrawCanvas(true));
                                dispatch(updateSelectedDrawingTab(DrawingTabEnum.SELECTED_SHAPE_TAB));
                                canvas.moveCursor = CANVAS_CURSOR.DEFAULT.GRABBING;
                            } else if (canvas.freeDrawingCursor === CANVAS_CURSOR.FREE_DRAWING.DRAW) {
                                // if we are free drawing, do this (better way to test this?)
                                dispatch(setNeedRedrawCanvas(true));
                                movingItem.current = undefined;
                            } else if (!skipShapeLogic.current) {
                                movingItem.current = shape;
                                drawingTool.removeObjectByName(canvas, CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE);
                            }
                        }
                    });
                }
            } else if (selectedDrawingTabRef.current !== DrawingTabEnum.FREE_DRAWING_TAB) {
                // discard any active object
                canvas.discardActiveObject();

                draggingState.current = DRAGGING_STATE.DRAGGING;
                lastPosition.current = { x: e.e.clientX, y: e.e.clientY };
            }
        },
        [
            canvas,
            dispatch,
            handleCornerSelection,
            handleEdgeSelection,
            updateActiveDrawingAppliance,
            updateActiveDrawingShape,
            updateDrawingShapes,
        ]
    );

    const onMouseUp = useCallback(
        (e: fabric.IEvent<MouseEvent>) => {
            // move the shape once dragging is done
            if (movingItem.current) {
                const pointer = new Vec2(canvas.getPointer(e.e));
                const moveVec = shapeStartDrag.current.subtract(pointer, true);
                if (typeof movingItem.current === 'object') {
                    dispatch(setPreventRepricing(true));
                    updateShapeByTranslation(moveVec, movingItem.current);
                    updateDrawingShapes([...drawingShapesRef.current]);
                    dispatch(updateSelectedDrawingTab(DrawingTabEnum.SELECTED_SHAPE_TAB));
                } else if (moveVec.x || moveVec.y) {
                    // moving an already selected appliance
                    dispatch(setPreventRepricing(true));
                    updateApplianceByTranslation(moveVec, movingItem.current, drawingShapesRef.current);
                    updateDrawingShapes([...drawingShapesRef.current]);
                }
                dispatch(setNeedRedrawCanvas(true));
                movingItem.current = undefined;
            } else {
                // set the draggingState to idle when the mouse up event is detected
                draggingState.current = DRAGGING_STATE.IDLE;
            }
        },
        [canvas, dispatch, updateApplianceByTranslation, updateDrawingShapes]
    );

    const onMouseMove = useCallback(
        (e: fabric.IEvent<MouseEvent>) => {
            if (draggingState.current === DRAGGING_STATE.DRAGGING && e && e.e) {
                // calculate deltas - manually calculated for cross-browser compatibility
                let deltaX = 0;
                let deltaY = 0;
                if (lastPosition.current.x) {
                    deltaX = e.e.clientX - lastPosition.current.x;
                }
                if (lastPosition.current.y) {
                    deltaY = e.e.clientY - lastPosition.current.y;
                }
                // update the last X and Y values
                lastPosition.current = { x: e.e.clientX, y: e.e.clientY };

                const delta = new fabric.Point(deltaX, deltaY);
                canvas.relativePan(delta);

                canvas.defaultCursor = CANVAS_CURSOR.DEFAULT.GRABBING;
            }
        },
        [canvas]
    );

    useEffect(() => {
        if (canvas) {
            canvas.on('drop', onDrop);
            canvas.on('mouse:down:before', onMouseDownBefore);
            canvas.on('mouse:down', onMouseDown);
            canvas.on('mouse:up', onMouseUp);
            canvas.on('mouse:move', onMouseMove);
        }

        return () => {
            if (canvas) {
                canvas.off('drop', onDrop);
                canvas.off('mouse:down:before', onMouseDownBefore);
                canvas.off('mouse:down', onMouseDown);
                canvas.off('mouse:up', onMouseUp);
                canvas.off('mouse:move', onMouseMove);
            }
        };
    }, [canvas, onDrop, onMouseDown, onMouseDownBefore, onMouseMove, onMouseUp]);

    // container dimensions change (resize window, collapse sidebars, etc)
    useLayoutEffect(() => {
        if (windowHeight && canvas) {
            canvas.setWidth(width);
            canvas.setHeight(0.7 * windowHeight - DESIGN_COUNTERTOP_ACTIONS_HEIGHT);
            autoZoomPanToFit(canvas, true, true);
        }
    }, [width, windowHeight, canvas]);

    const onCornerDimensionEdit = (originalCorner: CornerInfo, shape: DrawingShape, size: number) => {
        const editedCorner = CornerInfo.clone(originalCorner);
        for (const [key, corner] of Object.entries(shape.cornerMap)) {
            if (corner.id === editedCorner.id) {
                setCornerRadiusSize(editedCorner, size ?? DEFAULT_CORNER_RADIUS);
                shape.cornerMap[key] = editedCorner;
            }
        }
        updateDrawingShapes([...drawingShapes]);
        dispatch(setNeedRedrawCanvas(true));
        canvas.renderAll();
    };

    const updateEdgeBumpOutDepth = (arrow: ArrowInfo, shape: DrawingShape) => {
        const outlineEdges = getOutlineEdges(shape);
        outlineEdges
            .filter((edge) => containsBumpOut(edge!) && edge?.bumpOut!.depth && edge.id === arrow.drawingEdgeId)
            .map((edge) => {
                if (edge?.bumpOut && arrow.size) {
                    edge.bumpOut.depth = arrow.size;
                }
            });
    };

    const { notifyIfOutOfBounds } = useSideItemBoundsNotification();
    const updateSideItemDepth = (arrow: ArrowInfo, shape: DrawingShape, enteredValue?: number) => {
        if (!arrow.size) {
            return;
        }
        const edge = shape.drawingEdges.find(
            (edge) =>
                (edge.sideItem.type === SideItemOption.WATERFALL || // TODO EZE-1448
                    edge.sideItem.type === SideItemOption.BACKSPLASH) &&
                edge.sideItem.depth &&
                edge.id === arrow.drawingEdgeId
        );
        if (!edge) {
            return;
        }
        const removedEdge = shape.removedElementsHistory?.edgesAndCorners?.find(
            (removed) => removed.drawingEdge.id === edge.id
        );
        if (!removedEdge) {
            return;
        }

        switch (edge.sideItem.type) {
            case SideItemOption.WATERFALL:
                notifyIfOutOfBounds(true, WATERFALL_INPUT_LIMIT, 'WATERFALL', enteredValue ?? arrow.size);
                break;
            case SideItemOption.BACKSPLASH:
                notifyIfOutOfBounds(true, BACKSPLASH_INPUT_LIMIT, 'BACKSPLASH', enteredValue ?? arrow.size);
                break;
            default:
                break;
        }
        const adjustedSize =
            edge.sideItem.type === SideItemOption.WATERFALL
                ? adjustValueForMinMax(
                      roundToArrowLengthGranularity(arrow.size),
                      WATERFALL_INPUT_LIMIT.MIN,
                      WATERFALL_INPUT_LIMIT.MAX
                  )
                : adjustValueForMinMax(
                      roundToArrowLengthGranularity(arrow.size),
                      BACKSPLASH_INPUT_LIMIT.MIN,
                      BACKSPLASH_INPUT_LIMIT.MAX
                  );

        // removedEdge.drawingEdge.sideItem.depth = adjustedSize;
        edge.sideItem.depth = adjustedSize;
        edge.sideItem.arrowsInfo.forEach((sideItemArrow) => {
            if (sideItemArrow.isSideItemPresent) {
                sideItemArrow.size = adjustedSize;
            }
        });
        removedEdge.drawingEdge = edge;
    };

    const updateDiagonalCornerDimension = (arrow: ArrowInfo, shape: DrawingShape) => {
        if (arrow.size === undefined) {
            return;
        }

        Array.from(Object.values(shape.cornerMap))
            .filter((corner) => corner.diagonalLength && corner.id === arrow.cornerId)
            .map((cornerInfo) => {
                if (arrow.size !== undefined) {
                    if (arrow.size === 0) {
                        cornerInfo.label = DIAGONAL_CORNER_OPTIONS.REMOVE_DIAGONAL_CORNER;
                        cornerInfo.productId = undefined;
                        removeDiagonalCornerOnShape(cornerInfo);
                    } else {
                        if (arrow.maximumLength && arrow.size > arrow.maximumLength) {
                            arrow.size = arrow.maximumLength;
                        }

                        cornerInfo.diagonalLength = arrow.size;
                    }
                }
            });
        // TODO(Alyson): This solves a problem in wich changes to diagonalLength
        // were not detected by React. We need to find a better way without
        // replacing the whole drawingShape.
        updateDrawingShapes([...drawingShapesRef.current]);
    };

    const onDimensionEdit = (editedArrow: ArrowInfo, shape: DrawingShape, enteredValue?: number) => {
        dispatch(addClearedShapeEditNotification(shape.id));

        const validNumber = editedArrow?.size !== undefined && !isNaN(editedArrow?.size) && editedArrow?.size > 0;
        if (validNumber) {
            if (editedArrow.isRadiusOut) {
                updateEdgeRadiusSagitta(editedArrow, shape);
            } else if (editedArrow.isBumpOut) {
                updateEdgeBumpOutDepth(editedArrow, shape);
            } else if (editedArrow.isSideItemPresent) {
                updateSideItemDepth(editedArrow, shape, enteredValue);
            } else if (editedArrow.isDiagonalCorner) {
                updateDiagonalCornerDimension(editedArrow, shape);
            }
        } else if (editedArrow.drawingEdgeId && editedArrow?.size === 0) {
            // remove custom side
            // TODO EZE-1448 - I don't think this is a case that will ever occur as size is never zero
            // the filter()[0] assumes there is always one and only one edge, I think it's written like this to avoid a null check
            const edge = shape.drawingEdges.filter((edge) => edge.id === editedArrow.drawingEdgeId)[0];
            edge.customSideType = undefined;
            edge.realRadius = undefined;
            if (edge.bumpOut) {
                edge.bumpOut.length = 0;
                edge.bumpOut.clockwiseRemainderLength = 0;
                edge.bumpOut.counterClockwiseRemainderLength = 0;
            }
        }
        if (editedArrow && !editedArrow.isSideItemPresent && validNumber) {
            updateShapeByEdge(editedArrow, shape, false);
        }

        updateDrawingShapes([...drawingShapes]);
        dispatch(setNeedRedrawCanvas(true));
    };

    const onBumpOutDimEdit = (editedArrow: ArrowInfo, shape: DrawingShape) => {
        const bumpOutEdgeIdParts = editedArrow.drawingEdgeId?.split('-');
        if (bumpOutEdgeIdParts) {
            const parentEdgeId = bumpOutEdgeIdParts[0];
            const bumpOutArrowType = bumpOutEdgeIdParts[1];
            const newSize = roundToArrowLengthGranularity(editedArrow!.size!);
            shape.drawingEdges
                .filter((edge) => edge.id === parentEdgeId)
                .forEach((edge) => {
                    let change;
                    if (edge.bumpOut) {
                        switch (bumpOutArrowType) {
                            case BUMPOUT_CLOCKWISE_REMAINDER.toString():
                                change = newSize - edge.bumpOut.clockwiseRemainderLength;
                                edge.bumpOut.clockwiseRemainderLength = newSize;
                                edge.bumpOut.counterClockwiseRemainderLength -= change;
                                break;
                            case BUMPOUT_LENGTH.toString():
                                change = newSize - edge.bumpOut.length;
                                edge.bumpOut.length = newSize;
                                edge.bumpOut.counterClockwiseRemainderLength -= change / 2;
                                edge.bumpOut.clockwiseRemainderLength -= change / 2;
                                break;
                            case BUMPOUT_COUNTERCLOCKWISE_REMAINDER.toString():
                                change = newSize - edge.bumpOut.counterClockwiseRemainderLength;
                                edge.bumpOut.counterClockwiseRemainderLength = newSize;
                                edge.bumpOut.clockwiseRemainderLength -= change;
                                break;
                        }
                    }
                });
        }
        dispatch(setPreventRepricing(true));
        updateDrawingShapes([...drawingShapes]);
        dispatch(setNeedRedrawCanvas(true));
    };

    const updateEdgeRadiusSagitta = (arrow: ArrowInfo, shape: DrawingShape) => {
        const outlineEdges = getOutlineEdges(shape);
        outlineEdges
            .filter((edge) => containsRadiusOut(edge!) && edge?.radiusSagitta && edge.id === arrow.drawingEdgeId)
            .map((edge) => {
                if (edge && arrow.size) {
                    edge.radiusSagitta = Math.min(arrow.size, MAX_RADIUS_SAGITTA);
                }
            });
    };

    /**Among other stuff this hook adds all shapes and appliances to the canvas.*/
    // eslint-disable-next-line sonarjs/cognitive-complexity
    useEffect(() => {
        //Added for the performance
        //if (!needRedrawCanvas) return;   // with this in place, the first radii circle click does not update canvas

        drawingTool.clearCanvas(canvas);
        if (!drawingShapes || !areThereShapesInTheDrawingArea || !selectedColor || !selectedColor.overlayColor) {
            return;
        }

        drawingTool.addDrawingShape(
            canvas,
            drawingShapes,
            onBumpOutDimEdit,
            isDraggingAppliance,
            activeDrawingAppliance,
            activeDrawingShape
        );
        drawingTool.addMeasurements(
            canvas,
            drawingShapes,
            selectedColor?.overlayColor,
            onDimensionEdit,
            activeDrawingShape
        );
        drawingTool.addCornerRadiiMeasurements(
            canvas,
            drawingShapes,
            selectedColor?.overlayColor,
            onCornerDimensionEdit,
            cornerRadii
        );
        drawingTool.addShapeText(canvas, drawingShapes, selectedColor?.overlayColor);

        let area = 0.0;
        drawingShapes.forEach((shape: DrawingShape) => {
            shape?.onTextChange?.subscribe((text: string) => {
                shape.label = text;
                updateDrawingShapes([...drawingShapes]);
                dispatch(setNeedRedrawCanvas(true));
            });
            // TODO remove this from fabricCanvas and update the state in useShapeArea
            area += calculateBaseShapeArea(shape, selectedMaterialAreaType);

            // add side item
            shape?.removedElementsHistory?.edgesAndCorners
                .filter((edgeAndCorners) => edgeAndCorners.drawingEdge?.sideItem?.depth > 0)
                .forEach((edgeAndCorners) => {
                    const label =
                        edgeAndCorners.drawingEdge?.sideItem?.type === SideItemOption.BACKSPLASH
                            ? BACKSPLASH_LABEL
                            : WATERFALL_LABEL;
                    drawingTool.drawRectangleSideItem(
                        canvas,
                        shape,
                        edgeAndCorners.appliedCorners,
                        edgeAndCorners.drawingEdge,
                        edgeAndCorners.drawingEdge?.sideItem?.depth,
                        selectedColor,
                        onDimensionEdit,
                        label, // TODO EZE-1448 WTF is this doing?
                        activeDrawingShape
                    );
                });
        });
        drawingTool.fillColor(canvas, selectedColor, drawingShapes, isDraggingAppliance, slabImgElement);

        dispatch(updatePrevCounterTopAreaQty(area));

        // add back any needed selection circles when redrawing the canvas
        if (areThereShapesInTheDrawingArea) {
            drawingTool.removeObjectByName(canvas, CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE);

            if (selectionCircles !== SELECTION_CIRCLES.FOR_NONE) {
                drawingShapes.map((drawingShape: DrawingShape) => {
                    if (drawingShape) {
                        if (isSelectedByUser) {
                            drawingTool.addCirclesToOutlinePointsOfShape(
                                canvas,
                                drawingShape,
                                selectedCornerRadiusOnCard!,
                                {
                                    includeInsideCorners: true,
                                    includeOutsideCorners: true,
                                    removeDiagonalCorner: false,
                                }
                            );
                        } else if (selectedDiagonalCorner) {
                            const cornerType = getCornerType(selectedDiagonalCorner);

                            const diagonalCornerType: DiagonalCornerType = {
                                includeInsideCorners: cornerType === DIAGONAL_CORNER_OPTIONS.INSIDE_CORNER,
                                includeOutsideCorners: cornerType === DIAGONAL_CORNER_OPTIONS.OUTSIDE_CORNER,
                                removeDiagonalCorner: cornerType === DIAGONAL_CORNER_OPTIONS.REMOVE_DIAGONAL_CORNER,
                            };

                            drawingTool.addCirclesToOutlinePointsOfShape(
                                canvas,
                                drawingShape,
                                selectedDiagonalCorner,
                                diagonalCornerType
                            );
                        } else if (selectedCustomSide) {
                            drawingTool.addCirclesToEdgesOfShape(canvas, drawingShape, selectedCustomSide);
                        }
                    }
                });
            }
        }

        canvas.renderAll();
        dispatch(setNeedRedrawCanvas(false));

        return () => {
            drawingShapesRef.current?.forEach((shape: DrawingShape) => {
                shape?.onTextChange?.unsubscribe();
            });
        };
    }, [
        areThereShapesInTheDrawingArea,
        drawingShapes,
        selectedColor,
        selectedEdge,
        activeDrawingAppliance,
        activeDrawingShape,
        selectedMaterialAreaType,
        // needRedrawCanvas,
        // canvas,
        // onBumpOutDimEdit,
        // isDraggingAppliance,
        // onDimensionEdit,
        // onCornerDimensionEdit,
        // cornerRadii,
        // slabImgElement,
        // dispatch,
        // updateDrawingShapes,
        // t,
        // selectionCircles,
        // isSelectedByUser,
        // selectedDiagonalCorner,
        // selectedCustomSide,
        // selectedCornerRadiusOnCard,
    ]);

    // add edge name
    useEffect(() => {
        if (selectedEdge && areThereShapesInTheDrawingArea && selectedColor?.overlayColor) {
            drawingTool.addEdgeName(
                canvas,
                selectedEdge?.consumerDesc ?? selectedEdge?.name,
                selectedColor.overlayColor,
                drawingShapes
            );
        }
        if (!pauseUndoStackUpdatesForSummary) {
            if (skipUndoStackUpdates) {
                dispatch(setSkipUndoStackUpdates(false));
            } else {
                const cShapes = drawingTool.cloneShapes(drawingShapes);
                if (cShapes[0]) {
                    pushDrawingShapesUndoStack(cShapes, selectedEdge);
                }
            }
        }
    }, [
        canvas,
        areThereShapesInTheDrawingArea,
        selectedEdge,
        drawingShapes,
        slabImgElement,
        applianceTriggerToggle,
        drawingShapes,
    ]);

    // on color change
    useEffect(() => {
        if (selectedColor && selectedColor.slabImageUrl && drawingShapes) {
            drawingTool.fillColor(canvas, selectedColor, drawingShapes, false, slabImgElement);
            dispatch(setNeedRedrawCanvas(true));
        }
    }, [slabImgElement, canvas, drawingShapes]);

    // on  corner option on card selected
    useEffect(() => {
        if (areThereShapesInTheDrawingArea) {
            drawingTool.removeObjectByName(canvas, CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE);

            drawingShapes.map((drawingShape: DrawingShape) => {
                if (drawingShape && isSelectedByUser) {
                    drawingTool.addCirclesToOutlinePointsOfShape(canvas, drawingShape, selectedCornerRadiusOnCard!, {
                        includeInsideCorners: true,
                        includeOutsideCorners: true,
                        removeDiagonalCorner: false,
                    });
                }
            });
        }
    }, [selectedCornerRadiusOnCard, isSelectedByUser, drawingShapes]);

    // on diagonal corner radius option on card selected
    useEffect(() => {
        if (areThereShapesInTheDrawingArea && selectedDiagonalCorner) {
            drawingTool.removeObjectByName(canvas, CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE);

            const cornerType = selectedDiagonalCorner
                ? getCornerType(selectedDiagonalCorner)
                : DIAGONAL_CORNER_OPTIONS.OUTSIDE_CORNER;

            const diagonalCornerType: DiagonalCornerType = {
                includeInsideCorners: cornerType === DIAGONAL_CORNER_OPTIONS.INSIDE_CORNER,
                includeOutsideCorners: cornerType === DIAGONAL_CORNER_OPTIONS.OUTSIDE_CORNER,
                removeDiagonalCorner: cornerType === DIAGONAL_CORNER_OPTIONS.REMOVE_DIAGONAL_CORNER,
            };

            drawingShapes.forEach((drawingShape: DrawingShape) => {
                if (drawingShape && selectedDiagonalCorner) {
                    drawingTool.addCirclesToOutlinePointsOfShape(
                        canvas,
                        drawingShape,
                        selectedDiagonalCorner,
                        diagonalCornerType
                    );
                }
            });
        }
    }, [areThereShapesInTheDrawingArea, canvas, drawingShapes, selectedDiagonalCorner]);

    // on custom side option on card selected
    useEffect(() => {
        if (areThereShapesInTheDrawingArea && selectedCustomSide) {
            drawingTool.removeObjectByName(canvas, CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE);

            drawingShapes.map((drawingShape: DrawingShape) => {
                if (drawingShape && selectedCustomSide) {
                    drawingTool.addCirclesToEdgesOfShape(canvas, drawingShape, selectedCustomSide);
                }
            });
        }
    }, [areThereShapesInTheDrawingArea, canvas, drawingShapes, selectedCustomSide]);

    // on side item selected
    useSideItems();

    useEffect(() => {
        if (selectedColor && activeDrawingItem && isDrawingShape(activeDrawingItem)) {
            drawingTool.selectShape({
                allShapes: drawingShapes,
                canvas,
                activeDrawingShape: activeDrawingItem,
                color: selectedColor.overlayColor,
            });
        }
    }, [selectedColor, needRedrawCanvas, selectedEdge, drawingShapes, activeDrawingItem, canvas]); // fix for EE-1111 bug re-open

    // free-draw add shape
    useEffect(() => {
        if (activeFreeDrawing?.length) {
            // create the skeleton, then clean it up first to avoid future errors
            const skData = cleanSkeletonPoints(generateSkeletonData(activeFreeDrawing, DEFAULT_DEPTH * 0.5));

            if (skData) {
                const outlineData = getOutlinePts(skData) || [];
                const newPoly = outlineData.map((corner) => {
                    return corner.coords || new Vec2(0, 0);
                });
                const destVector = autoPlaceShapeTranslation(newPoly);
                for (const skPoint of skData) {
                    skPoint.pos.subtract(destVector);
                }
                const dShape = drawingTool.buildShapeFromSkeleton(skData, false);
                // skip invalid shapes (undefined result)
                if (dShape) {
                    dShape.type = ShapeType.DRAWN_SHAPE; // buildShapeFromSkeleton sets the type to predefined, fix it
                    dShape.edge = selectedEdge; //assign selected edge
                    updateDrawingShapes((drawingShapes) => [...drawingShapes, dShape]);
                    trackAddShape({ shape: dShape });
                    updateActiveDrawingShape(dShape);
                }
                dispatch(setNeedRedrawCanvas(true));
            }
            // prevents free-drawn shape unwanted spawning upon return from Summary page
            dispatch(updateActiveFreeDrawing([]));
        }
    }, [dispatch, activeFreeDrawing, drawingShapes]);

    const tryZooming = useCallback(
        (countDown: number) => {
            // delay allowing the drawing of appliances to catch up so that they may be considered in bounding box calc
            if (!areThereShapesInTheDrawingArea) {
                canvas.setZoom(DEFAULT_ZOOM);
                return;
            }
            const isCentered = areThereShapesInTheDrawingArea;
            setTimeout(() => {
                if (countDown === 0 || drawingTool.allShapesAndAppliancesAreDrawn(canvas, drawingShapes)) {
                    autoZoomPanToFit(canvas, isCentered, isCentered);
                    canvas.renderAll();
                } else if (countDown > 0) {
                    tryZooming(countDown - 1);
                }
            }, AUTO_ZOOM_DELAY);

            //TODO: Find better way than timeouts to avoid race or at least use const for standart same timeout
        },
        [areThereShapesInTheDrawingArea, canvas, drawingShapes]
    );

    // auto zoom and pan if more than one shape, reset zoom if empty canvas
    useLayoutEffect(() => {
        if (drawingShapes && canvas && !isManualZoom) {
            tryZooming(AUTO_ZOOM_DELAY_LOOPS);
        }
    }, [canvas, isManualZoom, drawingShapes, drawingShapesRef, toggleTriggerZooming, tryZooming]);

    // set the height of the canvas to be 70% of the view port extracting the height of the bottom items (rotate, delete)
    /**
     * Keep <canvas> wrapped in a div.
     * For problem that caused removing this wrapper see {@link https://inscyth-inc.atlassian.net/browse/EZE-1125}
     * To see where I found the solution to wrap in a div {@link https://stackoverflow.com/questions/50784302/uncaught-notfounderror-failed-to-execute-insertbefore-on-node/59845764#59845764}
     * */
    return (
        <div>
            <canvas ref={canvasRef} id="fabric-canvas" height={0.7 * windowHeight - DESIGN_COUNTERTOP_ACTIONS_HEIGHT} />
        </div>
    );
};

export const useFabricCanvasMethods = () => {
    const canvas = useAppSelector(selectFabricCanvas);
    const { selectedValue: selectedEdge } = useSelectedEdge();

    const dispatch = useAppDispatch();

    const {
        drawingShapes,
        updateDrawingShapes,
        addMultipleDrawingShapes,
        updateActiveDrawingShape,
        autoPlaceShapeTranslation,
    } = useContext(DrawingShapeContext);

    const addPredefinedShape = (predefinedShape: PredefinedShape) => {
        const selectedPredefinedShape = predefinedShape;
        if (canvas && selectedPredefinedShape) {
            const dShapes = drawingTool.buildShapeFromPredefinedShape(selectedPredefinedShape);

            // assign selected edge to all the new shapes - eliminates a useEffect that tries to clean this up later
            dShapes.forEach((shape) => {
                shape.edge = selectedEdge;
            });

            // if there isn't any shape selected or the case when it was previously selected a shape or free drawn
            if (drawingShapes.length === 0) {
                updateDrawingShapes([...dShapes]);
            } else {
                let newPoly;
                if (dShapes.length === 1) {
                    newPoly = dShapes[0].highlightPoints ?? [];
                } else {
                    // get bounding box of all shapes
                    newPoly = MathFunctions.findBoundingBox(
                        dShapes
                            .map((shape) => {
                                return shape.highlightPoints ?? [];
                            })
                            .flat(1)
                    ).asPointArray();
                }
                const destVector = autoPlaceShapeTranslation(newPoly);
                for (const shape of dShapes) {
                    updateShapeByTranslation(destVector, shape);
                }

                addMultipleDrawingShapes(dShapes);
                updateActiveDrawingShape(dShapes[0]);
            }
            dispatch(setNeedRedrawCanvas(true));
        }
    };

    return { addPredefinedShape };
};

export default FabricCanvas;
